/**
 * SCRUM-1032 — the half-filled station rows, kept across sittings.
 *
 * A station only reaches the server once every required field is answered, so
 * a row with a name and an area but no plant yet exists nowhere but this tab.
 * On a list of a thousand that is most of the work, most of the time: closing
 * the tab at that point would throw away an afternoon.
 *
 * So the incomplete rows are held locally until they are complete enough to be
 * written. Keyed by decoder address rather than by row id — the drafts are
 * rebuilt from the decoder list on every visit and their temp ids are not
 * stable, but a decoder address is exactly what identifies the station that
 * row is going to become.
 *
 * Only what the user entered is stored, not the whole row: the decoder half is
 * rebuilt from the decoders, so keeping a copy of it would only go stale.
 */

const STORAGE_PREFIX = 'cisgenics.guided.stationDrafts'

/** The fields a user can fill in on a draft row. Nothing else is persisted. */
const PERSISTED_FIELDS = [
  'description',
  'stationName',
  'stationType',
  'area',
  'subArea',
  'subAreaId',
  'plant',
  'plantId',
  'soil',
  'soilId',
  'emitter',
  'emitterId',
  'pipe',
  'pipeId',
  'expectedFlow',
  'precipitationRate',
  'priority',
  'defaultRunTime',
  'defaultWater',
  'microClimate',
  'etZone',
  'etZoneId',
] as const

/**
 * A cap, so a runaway list cannot fill the origin's storage quota and start
 * throwing on every write. Well above any real controller.
 */
const MAX_ENTRIES = 2000

export type StationDraftValues = Record<string, unknown>
/** Keyed by `address-index`. */
export type StoredStationDrafts = Record<string, StationDraftValues>

const storageKey = (interfaceId: number | string) =>
  `${STORAGE_PREFIX}.${interfaceId}`

/** Only the fields the user can answer, and only those actually carrying one. */
export const pickPersistedFields = (row: any): StationDraftValues => {
  const values: StationDraftValues = {}
  PERSISTED_FIELDS.forEach((field) => {
    const value = row?.[field]
    if (value === undefined || value === null || value === '') return
    values[field] = value
  })
  return values
}

/**
 * The staged rows worth remembering, and only the fields the user changed.
 *
 * Every row still staged is remembered, including the complete ones. Skipping
 * those on the grounds that the autosave was about to write them assumed the
 * write would succeed — and a complete row the server refuses is exactly the
 * row whose work must not be lost. Two such rows were left out of the store
 * and their area and sub-area went with the tab.
 *
 * A written row is retired from the drafts, so it never reaches this.
 *
 * What is left out is anything still equal to what the decoder produced.
 * Storing those would keep a copy of every untouched row (80KB for a thousand)
 * and, worse, hold a decoder's old name over the new one after a rename.
 */
export const collectDraftValues = (
  rows: any[],
  /** The freshly-built drafts, keyed by decoder address. */
  baseline: Record<string, any> = {},
): StoredStationDrafts => {
  const stored: StoredStationDrafts = {}
  rows.forEach((row) => {
    const address = String(row?.decoderAddress ?? '')
    if (!address || address === '-') return

    const values = pickPersistedFields(row)
    const base = baseline[address]
    if (base) {
      Object.keys(values).forEach((field) => {
        if (String(values[field]) === String(base[field] ?? '')) {
          delete values[field]
        }
      })
    }

    if (Object.keys(values).length === 0) return
    stored[address] = values
  })
  return stored
}

export const loadStationDrafts = (
  interfaceId: number | string,
): StoredStationDrafts => {
  try {
    const raw = window.localStorage.getItem(storageKey(interfaceId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as StoredStationDrafts
  } catch {
    // Unreadable or disabled storage is not worth an error: the step still
    // works, it just starts from the decoders again.
    return {}
  }
}

export const saveStationDrafts = (
  interfaceId: number | string,
  drafts: StoredStationDrafts,
) => {
  try {
    const keys = Object.keys(drafts)
    if (keys.length === 0) {
      window.localStorage.removeItem(storageKey(interfaceId))
      return
    }
    const capped =
      keys.length <= MAX_ENTRIES
        ? drafts
        : Object.fromEntries(keys.slice(0, MAX_ENTRIES).map((k) => [k, drafts[k]]))
    window.localStorage.setItem(storageKey(interfaceId), JSON.stringify(capped))
  } catch {
    // A full or disabled store must not break editing.
  }
}

export const clearStationDrafts = (interfaceId: number | string) => {
  try {
    window.localStorage.removeItem(storageKey(interfaceId))
  } catch {
    // Nothing to do.
  }
}

/**
 * Lays the remembered values back over the freshly-built drafts.
 *
 * The decoder half comes from the decoders every time, so a decoder renamed or
 * repurposed since the last sitting arrives current; only what the user typed
 * is restored on top. A remembered address with no draft — its station was
 * created, or its decoder is no longer a station decoder — is dropped.
 */
export const applyStoredDrafts = <T extends { decoderAddress: string }>(
  drafts: T[],
  stored: StoredStationDrafts,
): T[] =>
  drafts.map((draft) => {
    const values = stored[draft.decoderAddress]
    return values ? ({ ...draft, ...values } as T) : draft
  })
